import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from './Header';
import Pagination from './Pagination';
import './ManageEmployees.css';
import Profile from './icons/Profile.png';
import loader from './icons/loader.gif';
import arrow from './icons/arrows-up-down.png';
import EmployeeFormModal from './EmployeeFormModal';
import { API_ENDPOINTS } from './config/api.config';
import AddNewSidebar from "./AddNewSidebar";
import Drawer from '@mui/material/Drawer';



const PageSize = 10;
const DEBOUNCE_DELAY = 500;

function ManageEmployees() {
  const location = useLocation();
  const username = location.state?.username || '';

  // State for employees data
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sortOrder, setSortOrder] = useState('asc');


  // Debounce timer ref
  const debounceTimerRef = useRef(null);
  const filterRef = useRef(null);


  // State for location counts
  const [chennaiCount, setChennaiCount] = useState(0);
  const [cbeCount, setCbeCount] = useState(0);

  // State for modals
  const [showCreateDrawer, setShowCreateDrawer] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    employeeId: '',
    employeeName: '',
    email: '',
    projectName: '',
    leadId: null,
    shiftTime: null,
    location: null
  });

  // Snackbar ref
  const snackBar = useRef();
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [statusFilter, setStatusFilter] = useState("active");
  const [showFilter, setShowFilter] = useState(false);
  


  // Fetch employees on component mount and when page/search/status changes
  useEffect(() => {
    FetchEmployees(debouncedSearchQuery, currentPage - 1, statusFilter); // Backend uses 0-indexed pages
  }, [debouncedSearchQuery, currentPage, statusFilter]);

  // Debounce search query
  useEffect(() => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }


    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      if (searchQuery !== debouncedSearchQuery) {
        setCurrentPage(1);
      }
    }, DEBOUNCE_DELAY);

    // Cleanup on unmount
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowFilter(false);
      }
    };

    if (showFilter) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilter]);

  const FetchEmployees = async (searchTerm = '', page = 0, status = '') => {
    try {
      setIsLoading(true);

      // Build query params
      const params = new URLSearchParams();
      if (searchTerm) {
        params.append('searchTerm', searchTerm);
      }
      params.append('page', page.toString());
      // Add status filter parameter for backend filtering
      if (status) {
        params.append('status', status);
      }

      const response = await axios.get(`${API_ENDPOINTS.EMPLOYEE}?${params.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 120000,
      });

      const data = response.data;

      if (data?.employees) {
        // Sort by name initially
        data.employees.sort((a, b) => a.employeeName.localeCompare(b.employeeName));
        setEmployees(data.employees);
        setTotalCount(data.totalCount || data.employees.length);

        // Use location counts from API response
        setChennaiCount(data.chennai || 0);
        setCbeCount(data.coimbatore || 0);
      } else {
        setEmployees([]);
        setTotalCount(0);
      }

      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setIsLoading(false);
      setEmployees([]);
      setTotalCount(0);
    }
  };

  // Handle sort by name
  const HandleSortByName = () => {
    const newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    setSortOrder(newSortOrder);

    const sortedData = [...employees];
    sortedData.sort((a, b) => {
      return newSortOrder === 'asc'
        ? a.employeeName.localeCompare(b.employeeName)
        : b.employeeName.localeCompare(a.employeeName);
    });

    setEmployees(sortedData);
  };

  const HandleSortById = () => {
    const newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    setSortOrder(newSortOrder);

    const sortedData = [...employees];
    sortedData.sort((a, b) => {
      return newSortOrder === 'asc'
        ? a.employeeId.localeCompare(b.employeeId)
        : b.employeeId.localeCompare(a.employeeId);
    });

    setEmployees(sortedData);
  }


  // Data for display - filtering is now handled by backend API
  const currentTableData = useMemo(() => {
    return {
      data: employees,
      totalCount: totalCount
    };
  }, [employees, totalCount]);

  // Handle search - just update the search query, debounce will handle the rest
  const HandleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Handle form input change
  const HandleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Show snackbar
  const ShowSnackbar = (message, type = 'success') => {
    setSnackbarMessage(message);
    if (snackBar.current) {
      snackBar.current.className = `employee-snackbar show ${type}`;
      setTimeout(() => {
        if (snackBar.current) {
          snackBar.current.className = 'employee-snackbar';
        }
      }, 3000);
    }
  };

  // Open create drawer (changed from modal)
  const HandleOpenCreateDrawer = () => {
    setFormData({
      employeeId: '',
      employeeName: '',
      email: '',
      projectName: '',
      leadId: null,
      shiftTime: null,
      location: null,
    });
    setShowCreateDrawer(true);
  };

  // Open edit modal
  const HandleOpenEditModal = (employee) => {
    setSelectedEmployee(employee);
    setFormData({
      employeeId: employee.employeeId || '',
      employeeName: employee.employeeName || '',
      email: employee.email || '',
      projectName: employee?.project?.project || '',
      leadId: employee?.lead?.leadId || null,
      shiftTime: employee.shiftTime || null,
      location: employee.location || null
    });
    setShowEditModal(true);
  };

  // Open delete modal
  const HandleOpenDeleteModal = (employee) => {
    setSelectedEmployee(employee);
    setShowDeleteModal(true);
  };

  // Create employee
  const HandleCreateEmployee = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.employeeId || !formData.employeeName || !formData.projectName) {
      ShowSnackbar('Please fill all required fields (Employee ID, Name, Project)', 'error');
      return;
    }

    // Validate email format if provided
    if (formData.email) {
      const emailParts = formData.email.split('@');
      if (emailParts.length !== 2 || !emailParts[0] || !emailParts[1]?.includes('.')) {
        ShowSnackbar('Please enter a valid email address', 'error');
        return;
      }
    }

    try {
      // Prepare data: convert empty strings to null for shiftTime and leadId
      const dataToSend = {
        ...formData,
        shiftTime: formData.shiftTime || null,
        leadId: formData.leadId || null
      };

      const response = await axios.post(API_ENDPOINTS.EMPLOYEE, dataToSend, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.data) {
        ShowSnackbar('Employee created successfully!', 'success');
        setShowCreateDrawer(false);
        FetchEmployees(debouncedSearchQuery, currentPage - 1, statusFilter); // Refresh the list with current filters
      }
    } catch (error) {
      console.error('Error creating employee:', error);
      const errorMessage = error.response?.data?.message || 'Error creating employee. Please try again.';
      ShowSnackbar(errorMessage, 'error');
    }
  };
  const validateEmployeeForm = (formData) => {
    if (!formData.employeeId || !formData.employeeName || !formData.projectName) {
      return 'Please fill all required fields (Employee ID, Name, Project)';
    }

    if (formData.email) {
      const emailParts = formData.email.split('@');
      if (
        emailParts.length !== 2 ||
        !emailParts[0] ||
        !emailParts[1]?.includes('.')
      ) {
        return 'Please enter a valid email address';
      }
    }

    return null; // no errors
  };
  const prepareEmployeePayload = (formData) => ({
    ...formData,
    shiftTime: formData.shiftTime || null,
    leadId: formData.leadId || null
  });
  // Update employee
  const HandleUpdateEmployee = async (e) => {
    e.preventDefault();

    const errorMessage = validateEmployeeForm(formData);

    if (errorMessage) {
      ShowSnackbar(errorMessage, 'error');
      return;
    }

    try {
      const dataToSend = prepareEmployeePayload(formData);
      const response = await axios.put(
        `${API_ENDPOINTS.EMPLOYEE}/${selectedEmployee.employeeId}`,
        dataToSend,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data) {
        ShowSnackbar('Employee updated successfully!', 'success');
        setShowEditModal(false);
        setSelectedEmployee(null);
        FetchEmployees(debouncedSearchQuery, currentPage - 1, statusFilter); // Refresh the list with current filters
      }
    } catch (error) {
      console.error('Error updating employee:', error);
      const errorMessage = error.response?.data?.message || 'Error updating employee. Please try again.';
      ShowSnackbar(errorMessage, 'error');
    }
  };

  // Delete employee
  const HandleDeleteEmployee = async () => {
    try {
      const response = await axios.delete(
        `${API_ENDPOINTS.EMPLOYEE}/${selectedEmployee.employeeId}`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data || response.status === 200) {
        ShowSnackbar('Employee deleted successfully!', 'success');
        setShowDeleteModal(false);
        setSelectedEmployee(null);
        FetchEmployees(debouncedSearchQuery, currentPage - 1, statusFilter); // Refresh the list with current filters
      }
    } catch (error) {
      console.error('Error deleting employee:', error);
      ShowSnackbar('Error deleting employee. Please try again.', 'error');
    }
  };

  // Format time helper
  const FormatTime = (timeString) => {
    if (!timeString) return '-';
    const options = { hour: 'numeric', minute: 'numeric', hour12: true };
    const time = new Date(`2000-01-01 ${timeString}`);
    return time.toLocaleTimeString('en-US', options);
  };

  const HandleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1); // Reset to first page when filter changes
    setShowFilter(false);
  };


  return (
    <div style={{ position: "relative" }}>
      <Header />
      <div className="manage-employees-container">
        {/* Header Section */}
        <div className="manage-employees-header">
          <h2 className="manage-employees-title">Manage Employees</h2>
          <div className="user-profile">
            <img src={Profile} alt="Profile" />
            <span>{username.toUpperCase()}</span>
          </div>
        </div>
        <hr />

        {/* Location Tiles */}
        <div className="tiles-container">
          <div className="location-tile">
            <p className="tile-title">Chennai</p>
            <p className="tile-count">{chennaiCount}</p>
          </div>
          <div className="location-tile">
            <p className="tile-title">CBE</p>
            <p className="tile-count">{cbeCount}</p>
          </div>
        </div>

        {/* Search and Actions Bar */}
        <div className="employee-actions-bar">
          <div className="employee-search-container">
            <input
              type="text"
              placeholder="Search employee by name, ID, or email…"
              className="employee-search-input"
              value={searchQuery}
              onChange={HandleSearchChange}
            />

          </div>
          <div className='filter-wrap'>
            <div className="employee-filter" ref={filterRef}>
              <button
                type="button"
                className="add-btn"
                onClick={() => setShowFilter(!showFilter)}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 4h18l-7 8v6l-4 2v-8L3 4z"
                    fill="currentColor"
                  />
                </svg>
                <span>Filter</span>
              </button>

              {showFilter && (
                <div className="filter-dropdown">
                  <button type="button" className='employee-select-btn' tabIndex={0} onClick={() => HandleStatusFilter("active")} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); HandleStatusFilter("active"); } }}>Active</button>
                  <button type="button" className='employee-select-btn' tabIndex={0} onClick={() => HandleStatusFilter("inactive")} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); HandleStatusFilter("inactive"); } }}>Inactive</button>
                  <button type="button" className='employee-select-btn' tabIndex={0} onClick={() => HandleStatusFilter("")} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); HandleStatusFilter(""); } }}>All</button>
                </div>
              )}
            </div>
            <button className="add-employee-btn" onClick={HandleOpenCreateDrawer}><span style={{ fontSize: '20px', fontWeight: 'bold' }}>+</span>Add New</button>
            
          </div>
        </div>

        {/* Employee Table */}
        <div className="employee-table-container">
          {isLoading ? (
            <div className="loading-container">
              <img src={loader} alt="Loading..." />
            </div>
          ) : (
            <>
              {currentTableData.data.length > 0 ? (
                <table className="employee-table">
                  <thead>
                    <tr>
                      <th onClick={HandleSortById} style={{ cursor: 'pointer', minWidth: '80px' }}>Emp ID {sortOrder === 'asc' ? <img src={arrow} alt="Ascending" /> : <img src={arrow} alt="Descending" />}</th>
                      <th onClick={HandleSortByName} style={{ cursor: 'pointer' }} className='employee-name-header'>
                        Name {sortOrder === 'asc' ? <img src={arrow} alt="Ascending" /> : <img src={arrow} alt="Descending" />}
                      </th>
                      <th>Email</th>
                      <th>Project</th>
                      <th>Lead</th>
                      <th>Shift</th>
                      <th>Location</th>
                      <th>Employment Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentTableData.data.map((employee) => (
                      <tr key={employee.employeeId}>
                        <td>{employee.employeeId}</td>
                        <td>{employee.employeeName}</td>
                        <td>{employee.email || '-'}</td>
                        <td>{employee?.project?.project ?? '-'}</td>
                        <td>{employee?.lead?.leadName ?? '-'}</td>
                        <td>{FormatTime(employee.shiftTime) || '-'}</td>
                        <td>{employee.location || '-'}</td>
                        <td>{employee.isActive ? 'Active' : 'Inactive'}</td>
                        <td>
                          {employee.isActive && <div className="action-buttons">
                            <button
                              className="action-btn edit-btn"
                              onClick={() => HandleOpenEditModal(employee)}
                              title="Edit Employee"
                            >
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1DA1F2" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                            </button>
                            <button
                              className="action-btn delete-btn"
                              onClick={() => HandleOpenDeleteModal(employee)}
                              title="Delete Employee"
                            >
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#D92728" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                <line x1="10" y1="11" x2="10" y2="17" />
                                <line x1="14" y1="11" x2="14" y2="17" />
                              </svg>
                            </button>
                          </div>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="noRecords">No employees found!</p>
              )}
              <Pagination
                className="pagination-bar"
                currentPage={currentPage}
                totalCount={currentTableData.totalCount}
                pageSize={PageSize}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </>
          )}
        </div>

        {/* Create Employee Drawer */}
        <Drawer
          anchor="right"
          open={showCreateDrawer}
          onClose={() => setShowCreateDrawer(false)}
        >
          <div style={{ width: '500px', padding: '20px' }}>
          <AddNewSidebar onClose={() => setShowCreateDrawer(false)} />
            <EmployeeFormModal
              title="Add New Employee"
              mode="add"
              formData={formData}
              employees={employees}
              onChange={HandleInputChange}
              onSubmit={HandleCreateEmployee}
              onClose={() => setShowCreateDrawer(false)}
              isDrawer={true}
            />
          </div>
        </Drawer>

        {/* Edit Employee Modal */}
        {showEditModal && (
          <EmployeeFormModal
            title="Edit Employee"
            mode="edit"
            formData={formData}
            employees={employees}
            onChange={HandleInputChange}
            onSubmit={HandleUpdateEmployee}
            onClose={() => setShowEditModal(false)}
            selectedEmployee={selectedEmployee}
            isDrawer={false}
          />
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="employee-modal-overlay">
            <div className="delete-modal">
              <div className="delete-modal-icon">
                <svg width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="#D92728" strokeWidth="2.5">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="6.5" x2="12" y2="12" strokeLinecap="round" />
                  <circle cx="12" cy="16" r="1.25" fill="#D92728" stroke="none" />
                </svg>
              </div>
              <h3>Delete Employee?</h3>
              <p>Are you sure you want to delete "{selectedEmployee?.employeeName}"? This action cannot be undone.</p>
              <div className="delete-modal-actions">
                <button className="btn-cancel" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button
                  className="btn-submit"
                  onClick={HandleDeleteEmployee}
                  style={{ backgroundColor: '#D92728' }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Snackbar */}
        <div ref={snackBar} className="employee-snackbar">{snackbarMessage}</div>
      </div>
    </div>
  );
}

export default ManageEmployees;